<template>
  <div class="ba-control-slider"
       :class="{ disabled, horizontal }">
    <div class="slider">
      <span class="dot"
            ref="sliderDot"
            :style="sliderDotStyle" />
    </div>
    <span v-if="useInput"
          class="direct-input">
      <input type="number"
             v-model="inputValue"
             @focus="onInputFocus"
             @blur="onInputBlur"
             @keyup.enter="setDirectInputing()">
    </span>
    <slot/>
  </div>
</template>

<script>
export default {
  name: 'BaControlSlider',
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    value: {
      type: Number
    },
    disabled: {
      type: Boolean,
      default: false
    },
    useInput: {
      type: Boolean,
      default: false
    },
    horizontal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      inputValue: 0,
      currentDotPosition: 0
    }
  },
  computed: {
    sliderDotStyle () {
      return this.horizontal ? { left: (this.currentDotPosition - 3) + 'px' } : { top: (this.currentDotPosition - 3) + 'px' }
    }
  },
  watch: {
    value (value) {
      this.syncValue()
    }
  },
  mounted () {
    const sliderDot = this.$refs.sliderDot
    const track = sliderDot.parentNode
    track.addEventListener('mousedown', this.onPointerDown)
    track.addEventListener('touchstart', this.onPointerDown)
    if (this.horizontal) {
      this._maxDotPosition = track.offsetWidth - sliderDot.offsetWidth
    } else {
      this._maxDotPosition = track.offsetHeight - sliderDot.offsetHeight
    }
    this._maxDotPosition += 6
    this.syncValue()
  },
  methods: {
    setDirectInputing () {
      let value = Math.min(this.max, Math.max(this.min, parseInt(this.inputValue)))
      this.emitChange(value)
    },
    syncValue () {
      this.inputValue = this.value
      this.setCurrentDotPosition()
    },
    setCurrentDotPosition () {
      const position = Math.round(this._maxDotPosition * (this.value - this.min) / (this.max - this.min))
      this.currentDotPosition = (this.horizontal ? position : this._maxDotPosition - position)
    },
    calculateValue (position) {
      if (!this.horizontal) {
        position = this._maxDotPosition - position
      }
      let value = Math.round(this.min + ((this.max - this.min) * position / this._maxDotPosition))
      this.emitChange(value)
    },
    emitChange (value) {
      if (this.value !== value) {
        this.$emit('change', value)
      }
    },
    onInputFocus () {
      this.$emit('changingStart')
    },
    onInputBlur () {
      this.$emit('changingEnd')
    },
    onPointerDown (e) {
      this._downPoint = this.getEventPoint(e)[this.horizontal ? 0 : 1]
      const offsetPosition = this._downPoint - e.currentTarget.getBoundingClientRect()[this.horizontal ? 'left' : 'top'] - 3
      const position = Math.max(0, Math.min(this._maxDotPosition, offsetPosition))
      this._downDotPosition = position
      this.calculateValue(position)
      document.addEventListener('mousemove', this.onPointerMove)
      document.addEventListener('touchmove', this.onPointerMove, { passive: false })
      document.addEventListener('mouseup', this.onPointerUp)
      document.addEventListener('touchend', this.onPointerUp)
      this.$emit('changingStart')
      e.preventDefault()
    },
    onPointerMove (e) {
      let position = Math.max(0, Math.min(this._maxDotPosition, this._downDotPosition + (this.getEventPoint(e)[this.horizontal ? 0 : 1] - this._downPoint)))
      this.calculateValue(position)
      e.preventDefault()
    },
    onPointerUp () {
      document.removeEventListener('mousemove', this.onPointerMove)
      document.removeEventListener('touchmove', this.onPointerMove)
      document.removeEventListener('mouseup', this.onPointerUp)
      document.removeEventListener('touchend', this.onPointerUp)
      this.$emit('changingEnd')
    },
    getEventPoint (e) {
      if (e.touches) {
        e = e.touches[0] || e.changedTouches[0]
      }
      return [e.pageX || e.clientX, e.pageY || e.clientY]
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-control-slider {
  position: relative;
  width: 32px;
  padding: 12px 4px;
  box-sizing: border-box;
  display: block;

  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;
    > * {pointer-events: none;}
  }

  .slider {
    position: relative;
    width: 4px;
    height: 54px;
    margin: 0 auto;
    border-radius: 2px;
    box-shadow: inset 0 1px 1px 0 rgba(0, 0, 0, 0.15);
    background-color: #e9e9e9;
    display: block;
    cursor: pointer;

    &:before {
      position: absolute;
      left: -4px;
      right: -4px;
      top: 0;
      bottom: 0;
      content: '';
      background-color: transparent;
    }

    .dot {
      position: absolute;
      left: 50%;
      top: 0;
      width: 12px;
      height: 12px;
      margin-left: -6px;
      border-radius: 6px;
      box-shadow: 0 2px 5px -2px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.15);
      background-color: #fff;
      pointer-events: none;
    }
  }

  .direct-input {
    margin: 8px 0 -8px;
    display: block;

    input {
      width: 100%;
      height: 18px;
      font-size: 11px;
      color: #767676;
      line-height: 18px;
      text-align: center;
      padding: 1px 0 0;
      border-radius: 2px;
      border: 1px solid #e2e2e2;
      background-color: #f6f6f6;
      box-sizing: border-box;
      moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  &.horizontal {
    width: 105px;
    height: 30px;
    padding: 13px 0 0 12px;
    display: flex;

    .slider {
      width: 54px;
      height: 4px;
      margin: 0;

      &:before {
        left: 0;
        right: 0;
        top: -4px;
        bottom: -4px;
        content: '';
      }

      .dot {
        left: 0;
        top: 50%;
        margin-left: 0;
        margin-top: -6px;
      }
    }

    .direct-input {
      width: 24px;
      margin: -7px 0 0 8px
    }
  }
}
</style>
